"use client";

import { Music } from "@/lib/interfaces/group-card";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { Check, Music as MusicIcon, Pause, Play, Search, X } from "lucide-react";

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  categories: string[];
  musicList: Music[];
  onSelect: (music: Music) => void;
  /** The song currently on the card, shown as selected. */
  selectedId?: string | null;
}

/** Song picker: tap a song to use it, or the play button to listen first. */
const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  categories,
  musicList,
  onSelect,
  selectedId,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [playingId, setPlayingId] = useState<string | null>(null);

  const sheetRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const touchStartY = useRef(0);
  const touchCurrentY = useRef(0);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return musicList.filter(
      (m) =>
        (selectedCategory === "All" || m.category === selectedCategory) &&
        (!q || m.name.toLowerCase().includes(q) || m.category.toLowerCase().includes(q))
    );
  }, [musicList, searchQuery, selectedCategory]);

  const stopAudio = () => {
    audioRef.current?.pause();
    setPlayingId(null);
  };

  const close = () => {
    stopAudio();
    onClose();
  };

  // Close on Escape; stop any preview when the sheet goes away
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      audioRef.current?.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const togglePlay = (music: Music) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playingId === music.id && !audio.paused) {
      audio.pause();
      setPlayingId(null);
      return;
    }
    audio.src = music.music_file || "";
    audio.play().then(() => setPlayingId(music.id)).catch(() => setPlayingId(null));
  };

  const selectMusic = (music: Music) => {
    stopAudio();
    onSelect(music);
    onClose();
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchCurrentY.current = touchStartY.current;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    touchCurrentY.current = e.touches[0].clientY;
    const deltaY = touchCurrentY.current - touchStartY.current;
    if (deltaY > 0 && sheetRef.current) sheetRef.current.style.transform = `translateY(${deltaY}px)`;
  };
  const onTouchEnd = () => {
    if (!sheetRef.current) return;
    if (touchCurrentY.current - touchStartY.current > 100) close();
    sheetRef.current.style.transform = "translateY(0)";
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/40" onClick={close} aria-hidden="true" />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="fixed inset-x-0 bottom-0 z-50 mx-auto flex max-h-[85vh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:max-w-2xl"
      >
        <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
          <div className="mx-auto mt-2.5 h-1 w-12 rounded-full bg-paper-rule" aria-hidden="true" />
          <div className="flex items-center justify-between px-5 pb-3 pt-2 sm:px-6">
            <div>
              <h2 className="font-sans text-lg font-semibold text-ink">{title}</h2>
              <p className="text-[13px] text-gray-600">Tap a song to use it, or press play to listen first.</p>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-paper text-ink hover:bg-paper-linen"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="space-y-3 border-b border-paper-rule px-5 pb-3 sm:px-6">
          <label className="relative block">
            <span className="sr-only">Search songs</span>
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" aria-hidden="true" />
            <input
              type="search"
              placeholder="Search songs or categories"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-xl border border-paper-rule bg-white py-2.5 pl-10 pr-4 text-sm text-ink placeholder-gray-500 focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/15"
            />
          </label>
          <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                aria-pressed={selectedCategory === category}
                onClick={() => setSelectedCategory(category)}
                className={`shrink-0 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-colors ${
                  selectedCategory === category ? "bg-ink text-white" : "border border-paper-rule bg-white text-ink hover:bg-paper"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <ul className="flex-1 overflow-y-auto px-3 py-2 sm:px-4">
          {musicList.length === 0 && (
            <li className="space-y-2 p-2" aria-busy="true">
              {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-paper" />)}
            </li>
          )}
          {musicList.length > 0 && filtered.length === 0 && (
            <li className="px-3 py-10 text-center text-sm text-gray-600">No songs match your search.</li>
          )}
          {filtered.map((music) => {
            const isSelected = selectedId === music.id;
            const isPlaying = playingId === music.id;
            return (
              <li key={music.id} className={`flex items-center gap-2 rounded-xl px-2 py-1.5 ${isSelected ? "bg-paper" : "hover:bg-paper/60"}`}>
                <button
                  type="button"
                  onClick={() => togglePlay(music)}
                  aria-label={isPlaying ? `Pause ${music.name}` : `Play ${music.name}`}
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${isPlaying ? "bg-heart-600 text-white" : "bg-paper-linen text-ink hover:bg-ink hover:text-white"}`}
                >
                  {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
                </button>
                <button type="button" onClick={() => selectMusic(music)} className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-1 py-1.5 text-left">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-ink">{music.name}</span>
                    <span className="block truncate text-xs text-gray-600">{music.category}</span>
                  </span>
                  {isSelected ? (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-ink px-3 py-1.5 text-xs font-semibold text-white">
                      <Check className="h-3.5 w-3.5" aria-hidden="true" /> Selected
                    </span>
                  ) : (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-paper-rule bg-white px-3 py-1.5 text-xs font-semibold text-ink">
                      <MusicIcon className="h-3.5 w-3.5" aria-hidden="true" /> Use this song
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <audio ref={audioRef} onEnded={() => setPlayingId(null)} className="hidden" />
    </>
  );
};

export default BottomSheet;
